import { useTranslation } from "@sagot/i18n";
import { cn, Popover, PopoverContent, PopoverTrigger } from "@sagot/ui";
import { useRef, type KeyboardEvent } from "react";
import { edgeId } from "./canvas-layout";
import { ConnectionMenu } from "./connection-menu";
import { useConnectors } from "./connector-context";
import type { ExitFallback, ExitRef, ExitSlot } from "../exit-slots";

const K = "engage.workflows.connectors";

/** The dot of a handle: filled when connected, a ring when not, warning-coloured while a path is still needed or the path is gone. */
function dotClass(connected: boolean, broken: boolean): string {
  if (connected) return "border-primary-ink bg-primary-ink";
  return broken ? "border-warning bg-warning-soft" : "border-primary-ink bg-surface";
}

interface CanvasHandleProps {
  source: ExitRef;
  slot: ExitSlot;
  /** The step's name ("Reply buttons"), for the handle's accessible name. */
  stepName: string;
  /** What the exit does when it is not connected, as shown (the last step has no next step). */
  fallback: ExitFallback;
}

/**
 * The handle on an exit: a 24 px button on the tile's right border. Drag it onto a path to connect it, or onto empty
 * canvas for a new path; click or press Enter for the connection menu; Del or Backspace deletes the connection.
 */
export function CanvasHandle({ source, slot, stepName, fallback }: CanvasHandleProps) {
  const { t } = useTranslation();
  const api = useConnectors();
  const keepFocusOnClose = useRef(true);
  const id = edgeId(source.path, source.index, source.key);
  const connected = slot.target !== undefined && api.hasPath(slot.target);
  const missing = slot.target !== undefined && !connected;
  const name = api.exitName(source);
  const where = { exit: name, step: stepName, path: api.pathTitle(source.path) };
  const label = connected
    ? t(`${K}.handle.connected`, { ...where, target: api.pathTitle(slot.target ?? "") })
    : missing
      ? t(`${K}.handle.missing`, { ...where, id: slot.target })
      : t(`${K}.handle.${fallback}`, where);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if ((event.key !== "Delete" && event.key !== "Backspace") || !connected || !api.editable) return;
    event.preventDefault();
    api.disconnect(source);
  };
  return (
    <Popover open={api.menuFor === id} onOpenChange={(open) => { api.openMenu(open ? id : null); }}>
      <PopoverTrigger
        asChild
        onClick={(event) => { if (api.takeSwallowedClick()) event.preventDefault(); }}
      >
        <button
          type="button"
          aria-label={label}
          aria-keyshortcuts={connected && api.editable ? "Delete Backspace" : undefined}
          data-testid="canvas-handle"
          data-exit={source.key}
          data-connected={connected ? "true" : "false"}
          className="absolute right-[var(--geo-handle-x)] top-1/2 flex size-[var(--geo-handle)] -translate-y-1/2 touch-none items-center justify-center rounded-pill hover:bg-primary-soft"
          onFocus={() => { if (connected) api.focus(id); }}
          onBlur={() => { api.focus(null); }}
          onKeyDown={onKeyDown}
          {...api.bind(source, "handle")}
        >
          <span aria-hidden="true" className={cn("block size-3 rounded-pill border-2", dotClass(connected, missing || fallback === "unset"))} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-2"
        collisionPadding={8}
        aria-label={t(`${K}.menu.label`, { exit: name })}
        data-testid="connection-menu"
        onCloseAutoFocus={(event) => {
          if (keepFocusOnClose.current) return;
          event.preventDefault();
          keepFocusOnClose.current = true;
        }}
      >
        <ConnectionMenu
          source={source}
          target={slot.target}
          onGoTo={(tileId) => {
            keepFocusOnClose.current = false;
            api.goTo(tileId);
          }}
          onClose={() => { api.openMenu(null); }}
        />
      </PopoverContent>
    </Popover>
  );
}
